/**
 * Reusable JSON-LD builders for SEO + GEO (Generative Engine Optimization).
 * These structured-data blocks help LLMs (ChatGPT, Perplexity, Claude, Gemini)
 * understand and cite the site as a source.
 */

const BASE_URL = "https://preime.asirox.com";

export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Asirox",
    url: BASE_URL,
    logo: `${BASE_URL}/asirox-logo.png`,
    slogan: "Conscious Engineering · Belleza atrevida, impulsada por la ciencia",
    description:
      "Fabricante español premium de plataformas dermo faciales profesionales: hidradermoabrasión, radiofrecuencia, ultrasonidos, microcorrientes y microneedling para clínicas estéticas, centros de belleza y franquicias.",
    foundingDate: "2003",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Calle Rocafort 89",
      addressLocality: "Sabadell",
      addressRegion: "Barcelona",
      postalCode: "08205",
      addressCountry: "ES",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        telephone: "+34 935 70 49 54",
        email: "info@asirox.com",
        areaServed: ["ES", "PT", "FR", "IT", "MX", "AR", "CO", "CL", "PE", "AE"],
        availableLanguage: ["Spanish", "English", "French"],
      },
    ],
    sameAs: [
      "https://instagram.com/asiroxofficial",
      "https://linkedin.com/company/asiroxl",
    ],
  };
}

export function websiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Asirox",
    url: BASE_URL,
    inLanguage: "es-ES",
    publisher: { "@type": "Organization", name: "Asirox" },
    potentialAction: {
      "@type": "SearchAction",
      target: `${BASE_URL}/blog-dermo-facial?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http") ? it.url : `${BASE_URL}${it.url}`,
    })),
  };
}

export function productLd(machine) {
  if (!machine) return null;
  const url = `${BASE_URL}/equipos-dermo-faciales/${machine.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: machine.name,
    description: machine.short_description || machine.tagline,
    brand: { "@type": "Brand", name: "Asirox" },
    manufacturer: { "@type": "Organization", name: "Asirox", url: BASE_URL },
    category: machine.category || "Equipo dermo facial profesional",
    image: machine.hero_image,
    url,
    sku: machine.slug,
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Clínicas estéticas, centros de belleza, medicina estética y franquicias",
    },
    offers: {
      "@type": "Offer",
      url: `${BASE_URL}/contacto?intent=quote&modelo=${machine.slug}`,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "Asirox" },
      priceSpecification: {
        "@type": "PriceSpecification",
        description: "Precio cotizado a medida según configuración, formación y volumen.",
        priceCurrency: "EUR",
      },
    },
    additionalProperty: [
      machine.wavelength && { "@type": "PropertyValue", name: "Tecnología", value: machine.wavelength },
      machine.power && { "@type": "PropertyValue", name: "Potencia", value: machine.power },
      machine.spot_size && { "@type": "PropertyValue", name: "Pieza de mano", value: machine.spot_size },
      machine.frequency && { "@type": "PropertyValue", name: "Frecuencia", value: machine.frequency },
      machine.cooling && { "@type": "PropertyValue", name: "Refrigeración", value: machine.cooling },
    ].filter(Boolean),
  };
}

export function articleLd(article) {
  if (!article) return null;
  const url = `${BASE_URL}/blog-dermo-facial/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.hero_image,
    inLanguage: "es-ES",
    datePublished: article.created_at || article.date,
    dateModified: article.updated_at || article.created_at || article.date,
    author: { "@type": "Organization", name: "Asirox" },
    publisher: {
      "@type": "Organization",
      name: "Asirox",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/asirox-logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    keywords: (article.tags || []).join(", "),
  };
}

export function faqLd(faqs) {
  if (!faqs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** Common FAQs used on Home, also great for ChatGPT / Perplexity citations. */
export const COMMON_FAQS = [
  {
    q: "¿Qué equipos dermo faciales fabrica Asirox?",
    a: "Asirox fabrica cuatro plataformas dermo faciales profesionales: VEGA Pro (hidradermoabrasión + LED), NOVA Triwave (radiofrecuencia bipolar + ultrasonidos + microcorrientes), AXIS Med (RF fraccional + microneedling, CE médico IIb) y Préime DermaFacial (5 tecnologías combinadas en una sola plataforma).",
  },
  {
    q: "¿Asirox tiene equipos de depilación láser?",
    a: "No. El catálogo actual de Asirox es exclusivamente dermo facial: hidradermoabrasión, radiofrecuencia facial, ultrasonidos, microcorrientes y microneedling. No comercializamos depilación láser.",
  },
  {
    q: "¿Cuál es el equipo dermo facial Asirox más vendido?",
    a: "Préime DermaFacial es la plataforma flagship: integra 5 tecnologías (AquaB hidradermoabrasión, Vibrox microdermoabrasión, MicroT microcorrientes, Collagen+ radiofrecuencia y UltraB ultrasonidos) y es la opción más completa para clínicas estéticas premium.",
  },
  {
    q: "¿Los equipos Asirox están certificados?",
    a: "Sí. Todos los modelos cuentan con marcado CE. AXIS Med dispone además de certificación CE Médico clase IIb por su radiofrecuencia fraccional y microneedling para uso clínico.",
  },
  {
    q: "¿Dónde se fabrican los equipos Asirox?",
    a: "En España. Sede e ingeniería propia en Sabadell (Barcelona). Fabricación nacional con soporte técnico en idioma local para España, Portugal, LATAM y resto de Europa.",
  },
  {
    q: "¿Cómo solicitar una demostración de un equipo dermo facial Asirox?",
    a: "Desde la página de contacto (https://preime.asirox.com/contacto) o llamando al +34 935 70 49 54. Asirox organiza demostraciones presenciales en su sede de Sabadell o desplazamientos a clínicas en España y Portugal.",
  },
];
